import React, { useEffect, useState, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import * as Icon from "react-feather";
import { CustomSelectDropdown } from "../../atoms/Inputs";

import { LinkButton, PrimaryButton } from "../../atoms/Buttons";
import { H4, P } from "../../atoms/Texts";
import { DarkFormField, DarkTextArea } from "../../atoms/Inputs";
import organization_categories from "../../../shared/fixtures/organization_categories";
import ntee_classification from "../../../shared/fixtures/ntee_classification";
import { getNewToken } from "../../../shared/helpers/util.country";
import BasicOrgDetails from "../../molecules/Forms/BasicOrgDetails";
import ContactsForms from "../../molecules/Forms/ContactsForms";
import { AddPhone } from "../../molecules/Forms/AddPhones";
import LocationForms from "../../molecules/Forms/LocationsForms";
import { selectStyle } from "../../../shared/fixtures/select_style";

const generateKey = () => Date.now();

const FormOne = ({ setCurrentStep, updateFields }) => {
  const [, setCountries] = useState([]);
  const formCtx = useFormContext();

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors: formStateErrors },
  } = formCtx;

  const selectedCategory = watch("opCategory");

  const categories = useMemo(
    () =>
      organization_categories.map((cat) => ({
        label: cat.category,
        value: cat.category,
      })),
    [],
  );

  // Get subcategories based on selected category
  const subCategories = useMemo(() => {
    if (!selectedCategory?.value) return [];

    const selectedCat = organization_categories.find(
      (cat) => cat.category === selectedCategory.value,
    );

    return (
      selectedCat?.sub_categories.map((subCat) => ({
        label: subCat,
        value: subCat,
      })) || []
    );
  }, [selectedCategory]);

  // Handle category selection
  const handleCategoryChange = (selectedOption, onChange) => {
    onChange(selectedOption);
    // Reset the subcategory when category changes
    setValue("subOpCategory", "");
  };

  // Watch the nteeType field to update classifications
  const selectedType = watch("nteeType");

  // Transform the top-level classifications for the first dropdown
  const nteeTypes = useMemo(
    () =>
      ntee_classification.map((classification) => ({
        label: classification.top_level,
        value: classification.top_level,
        letter: classification.letter,
      })),
    [],
  );

  // Get classifications based on selected type
  const nteeClassifications = useMemo(() => {
    if (!selectedType?.value) return [];

    const selectedCategory = ntee_classification.find(
      (type) => type.top_level === selectedType.value,
    );

    return (
      selectedCategory?.lower_levels.map((level) => ({
        label: level.name,
        value: level.code,
        code: level.code,
      })) || []
    );
  }, [selectedType]);

  // Handle type selection
  const handleTypeChange = (selectedOption, onChange) => {
    onChange(selectedOption);
    // Reset the classification when type changes
    setValue("nteeClassification", "");
  };

  const updateData = (data) => {
    const formOneData = {
      ...data,
      nteeType: data.nteeType,
      nteeLetter: data.nteeType.letter,
      nteeCode: data.nteeClassification.code,
    };

    updateFields(formOneData);
    setCurrentStep(2);
  };

  const [proofInputArray, setProofInputArray] = useState([
    {
      id: generateKey(),
    },
  ]);

  const addNewProof = (id) => {
    const newList = [...proofInputArray, { id: generateKey() }];
    const newListSet = new Set(newList);

    setProofInputArray([...newListSet]);
  };

  const removeProof = (id) => {
    const newList = proofInputArray.filter((proofInput) => id !== proofInput.id);
    setProofInputArray(newList);
  };

  const getCountryToken = async () => {
    const countries = await getNewToken();
    setCountries(countries);
  };

  useEffect(() => {
    getCountryToken();
  }, []);

  return (
    <main>
      <form onSubmit={handleSubmit(updateData)} className="pb-10">
        <BasicOrgDetails />
        <hr />
        <H4 className="mb-4 mt-10">Verification Details</H4>
        <section className="grid grid-cols-1 gap-x-5">
          <div>
            <DarkTextArea
              rows={2}
              id="relationshipToOrganization"
              name="relationshipToOrganization"
              {...register("relationshipToOrganization", {
                required: "Please enter your relationship to the organization",
                minLength: {
                  value: 2,
                  message: "Please enter your relationship to the organization",
                },
              })}
              placeholder="Type your relationship to organization E.g: CEO, Co-Founder"
              label="Relationship to Organization"
              className="w-full"
              required
            />

            {formStateErrors.relationshipToOrganization && (
              <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm">
                {formStateErrors.relationshipToOrganization.message}
              </P>
            )}
          </div>

          {proofInputArray.map((proofInput, index) => (
            <div key={`proof_input_${proofInput.id}`}>
              <div className="flex items-center justify-between">
                <DarkFormField
                  type="text"
                  id={`proof_input_${proofInput.id}`}
                  name={`proof_input_${proofInput.id}`}
                  {...register(`proof_input_${proofInput.id}`, {
                    required: "Please enter link to organization website.",
                    pattern: {
                      value:
                        /^(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/,
                      message: "Please enter a valid link.",
                    },
                  })}
                  placeholder="E.g https://drive.get-organization.com"
                  label={index < 1 && "Proof of Relationship"}
                  containerClass="flex-grow"
                  required
                />

                {proofInputArray.length > 1 && (
                  <Icon.X
                    color="#E62250"
                    className={`ml-4 cursor-pointer ${index < 1 ? "" : "mb-6"}`}
                    onClick={() => removeProof(proofInput.id)}
                  />
                )}
              </div>

              {formStateErrors[`proof_input_${proofInput.id}`] && (
                <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm">
                  {formStateErrors[`proof_input_${proofInput.id}`].message}
                </P>
              )}
            </div>
          ))}

          <LinkButton
            type="button"
            onClick={addNewProof}
            className="flex items-center -mt-2 mb-4"
            style={{
              width: "fit-content",
            }}
            noOutline
          >
            Add another proof
            <Icon.Plus color="#442ECF" size={12} className="ml-2" />
          </LinkButton>
        </section>
        <hr />
        <H4 className="mb-4 mt-10">
          Categorization (Helps people easily find your organization)
        </H4>
        <section className="grid grid-cols-1 md:grid-cols-2 gap-x-5  mb-6 gap-y-8">
          <div>
            <label htmlFor="opCategory">
              <P fontSize="14px" fontFamily="semi" color="#47495C" className="mb-2">
                Category
                <span className="text-red-500">*</span>
              </P>
            </label>
            <Controller
              control={control}
              name="opCategory"
              id="opCategory"
              rules={{ required: true }}
              render={({ field }) => (
                <CustomSelectDropdown
                  {...field}
                  autoComplete="true"
                  styles={selectStyle}
                  className="w-full"
                  placeholder="--Select a category--"
                  options={categories}
                  onChange={(option) => handleCategoryChange(option, field.onChange)}
                />
              )}
            />
            {formStateErrors.opCategory && (
              <P fontSize="12px" className="mt-2 mb-4 text-red-400 text-sm">
                Select a category
              </P>
            )}
          </div>
          <div>
            <label htmlFor="subOpCategory">
              <P fontSize="14px" fontFamily="semi" color="#47495C" className="mb-2">
                Subcategory
                <span className="text-red-500">*</span>
              </P>
            </label>
            <Controller
              control={control}
              name="subOpCategory"
              id="subOpCategory"
              rules={{ required: true }}
              render={({ field }) => (
                <CustomSelectDropdown
                  {...field}
                  autoComplete="true"
                  styles={selectStyle}
                  className="w-full"
                  isDisabled={!selectedCategory}
                  placeholder={
                    !selectedCategory
                      ? "You have to select a category first"
                      : "--Select a sub category"
                  }
                  options={subCategories}
                />
              )}
            />
            {formStateErrors.subOpCategory && (
              <P fontSize="12px" className="mt-2 mb-4 text-red-400 text-sm">
                Select a sub category
              </P>
            )}
          </div>

          <div>
            <label
              htmlFor="nteeType"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              NTEE Type <span className="text-red-500">*</span>
            </label>
            <Controller
              control={control}
              name="nteeType"
              rules={{ required: true }}
              render={({ field }) => (
                <CustomSelectDropdown
                  {...field}
                  id="nteeType"
                  options={nteeTypes}
                  placeholder="--Select a type--"
                  className="w-full"
                  onChange={(option) => handleTypeChange(option, field.onChange)}
                />
              )}
            />
            {formStateErrors.nteeType && (
              <p className="mt-2 text-sm text-red-500">
                Select a type<span className="text-red-500">*</span>
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="nteeClassification"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              NTEE Classification <span className="text-red-500">*</span>
            </label>
            <Controller
              control={control}
              name="nteeClassification"
              rules={{ required: true }}
              render={({ field }) => (
                <CustomSelectDropdown
                  {...field}
                  id="nteeClassification"
                  options={nteeClassifications}
                  isDisabled={!selectedType}
                  placeholder={
                    !selectedType
                      ? "You have to select a type first"
                      : "--Select a classification--"
                  }
                  className="w-full"
                />
              )}
            />
            {formStateErrors.nteeClassification && (
              <p className="mt-2 text-sm text-red-500">
                Select a classification<span className="text-red-500">*</span>
              </p>
            )}
          </div>
        </section>
        <hr />
        <ContactsForms />
        <hr />
        <LocationForms />
        <AddPhone />
        <PrimaryButton>Continue</PrimaryButton>
      </form>
    </main>
  );
};

export default FormOne;
